import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, LOCALE_ID, inject } from "@angular/core";
import { STORE_NAME_GASES } from "@app/indexed-db-config";
import { Gas } from "@domain/gas/gas";
import { UserService } from "@domain/user/user.service";
import { environment } from "@environments/environment";
import { BusinessScope } from "@lib/constants/business-scope";
import { ObservableInstanceMapper } from "@utils/observable-instance-mapper";
import { instanceToPlain } from "class-transformer";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { Observable, catchError, forkJoin, map, of, switchMap, throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class GasService {
  private http = inject(HttpClient);
  private dbService = inject(NgxIndexedDBService);
  private localeId = inject(LOCALE_ID);
  private userService = inject(UserService);

  public static readonly GAS_URL = environment.apiUrl + "/gas/gases";
  private userScope!: BusinessScope;

  constructor() {
    this.userService.user$.subscribe((user) => {
      this.userScope = user.scope!;
    });
  }

  loadAll(): Observable<Gas[] | null> {
    return this.dbService.count(STORE_NAME_GASES).pipe(
      switchMap((count: number) => {
        if (count > 0) {
          return this.loadGasesFromDB().pipe(
            switchMap((gases) => {
              if (this.checkGasDataRevisions(gases)) {
                return of(gases);
              }
              return this.refreshAll();
            }),
          );
        }
        return this.refreshAll();
      }),
    );
  }

  private checkGasDataRevisions(gases: Gas[]) {
    return this.checkForProductSensors(gases) && this.checkForLocalizedNames(gases, this.localeId);
  }

  private checkForProductSensors(gases: Gas[]) {
    return !gases.some((gas) => !gas.productSensors);
  }

  private checkForLocalizedNames(gases: Gas[], key: string) {
    if (!gases[0].definition.localizedName) {
      return false;
    }
    return gases.every((gas) => key in gas.definition.localizedName);
  }

  refreshAll(): Observable<Gas[]> {
    if (this.localeId === "en-US") {
      return this.loadGasesFromApi(this.localeId).pipe(
        switchMap((gases) => this.clearAll().pipe(map(() => gases))),
        switchMap((gases) => this.storeGasesInDB(gases)),
      );
    }
    return forkJoin([this.loadGasesFromApi("en-US"), this.loadGasesFromApi(this.localeId)]).pipe(
      switchMap((gases) => this.clearAll().pipe(map(() => gases))),
      switchMap((gases) => this.storeGasesInDB(...gases)),
    );
  }

  getGasById(id: string): Observable<Gas | undefined> {
    return ObservableInstanceMapper.valueToInstance(this.dbService.getByKey<Gas>(STORE_NAME_GASES, id), Gas);
  }

  clearAll(): Observable<void> {
    return this.dbService.clear(STORE_NAME_GASES);
  }

  private loadGasesFromApi(languageString: string): Observable<Gas[]> {
    const params = new HttpParams().set("lang", languageString);

    return ObservableInstanceMapper.valuesToInstance(
      this.http.get<Gas[]>(GasService.GAS_URL, { params }).pipe(
        map((json: any) => json),
        catchError((err) => {
          if (this.userScope === BusinessScope.MONITORING) {
            return of([]);
          }
          return throwError(() => err);
        }),
      ),
      Gas,
    );
  }

  private loadGasesFromDB(): Observable<Gas[]> {
    return ObservableInstanceMapper.valuesToInstance(this.dbService.getAll<Gas>(STORE_NAME_GASES), Gas);
  }

  private storeGasesInDB(gasesEnglish: Gas[], gasesCurrentLocale?: Gas[]): Observable<Gas[]> {
    return this.dbService
      .bulkAdd(
        STORE_NAME_GASES,
        // get rid of unknown properties by adding excludeExtraneousValues option
        gasesEnglish.map((gas) => {
          gas.id = gas.definition.casNumber;
          gas.productSensors = gas.products_sensor;
          gas.definition.localizedName = { "en-US": gas.definition.name };
          if (gasesCurrentLocale) {
            const matchingGases = gasesCurrentLocale.filter(
              (localeGas) => localeGas.definition.casNumber === gas.definition.casNumber,
            );
            gas.definition.localizedName[this.localeId] =
              matchingGases.length === 1 ? matchingGases[0].definition.name : gas.definition.name;
          }
          return instanceToPlain(gas, { excludeExtraneousValues: true });
        }),
      )
      .pipe(map(() => gasesEnglish)); // return added gas instances not the plain ones from dbService.add
  }
}
